import React, { useEffect, useState } from "react";
import { Trophy, ChevronLeft, ChevronRight, Save, Upload } from "lucide-react";
import { useScoresStore } from "../../store/scoresStore";
import useAuthStore from "../../store/authStore";
import { PlayerRecord } from "../../components/PlayerRecord";
import { useConfirmationDialog } from "../../components/ConfirmDialog";
import { v4 as uuid } from "uuid";
import { formatToTimeStamp, showDateTimeShort } from "../../utils/dateUtils";
import { UserRoles } from "../../models/playerModel";
import { ScoreDataEntry } from "./ScoreDataEntry";
import { MatchPlayScoreEntry } from "./MatchPlayScoreEntry";
import { FlightDetails, Score } from "../../models/scoreModel";
import { useSeasonsStore } from "../../store/seasonsStore";
import { useMatchesStore } from "../../store/matchesStore";
import { MatchPlayScoreDataEntry } from "./MatchPlayScoreDataEntry";

export const ScoreEntry: React.FC<any> = () => {
  const { currentUser } = useAuthStore();
  if (!currentUser) return null;
  const { currentSeason, getCurrentSeason } = useSeasonsStore();
  const { inProgressMatch, getInProgressMatchSetup } = useMatchesStore();
  useEffect(() => {
    if (!currentSeason) {
      getCurrentSeason();
    }
  }, [getCurrentSeason, currentSeason]);

  useEffect(() => {
    if (currentSeason && !inProgressMatch) {
      getInProgressMatchSetup(currentSeason.id);
    }
  }, [currentSeason, getInProgressMatchSetup, inProgressMatch]);

  if (!currentSeason) return <div>loading season....</div>;

  if (!inProgressMatch) return <div>loading match....</div>;

  if (inProgressMatch.gameFormat === "matchplay") {
    if (currentUser.role === UserRoles.dataEntry) {
      return <MatchPlayScoreDataEntry />;
    } else {
      return <MatchPlayScoreEntry />;
    }
  } else {
    if (currentUser.role === UserRoles.dataEntry) {
      return <ScoreDataEntry />;
    } else {
      return <ScoreEntryPlayer />;
    }
  }
};

const ScoreTable: React.FC<{
  scores: any[];
  title: string;
  title2: string;
}> = ({ scores, title, title2 }) => (
  <div className="mb-6">
    <h4 className="text-md font-medium mb-2 text-gray-900 dark:text-gray-100">
      {title}
      {title2 && (
        <>
          <br />
          {title2}
        </>
      )}
    </h4>
    <div className="overflow-x-auto bg-white dark:bg-gray-800 rounded-lg">
      <table className="w-full">
        <thead>
          <tr className="text-left text-sm border-b border-gray-200 dark:border-gray-700">
            <th className="px-4 py-3 text-gray-600 dark:text-gray-300">Hole</th>
            <th className="px-4 py-3 text-gray-600 dark:text-gray-300">
              Score
            </th>
            <th className="px-4 py-3 text-gray-600 dark:text-gray-300">
              Status
            </th>
          </tr>
        </thead>
        <tbody>
          {scores.map((score) => (
            <tr
              key={score.id}
              className="border-b border-gray-100 dark:border-gray-700 last:border-b-0"
            >
              <td className="px-4 py-3 text-gray-900 dark:text-gray-100">
                {score.holeNumber}
              </td>
              <td className="px-4 py-3 text-gray-900 dark:text-gray-100">
                {score.score}
              </td>
              <td className="px-4 py-3">
                <span
                  className={`px-2 py-1 rounded-full text-xs ${
                    score.isUploaded
                      ? "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
                      : "bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-100"
                  }`}
                >
                  {score.isUploaded ? "Uploaded" : "Pending"}
                </span>
              </td>
            </tr>
          ))}
          {scores.length === 0 && (
            <tr>
              <td
                colSpan={3}
                className="px-4 py-3 text-center text-gray-500 dark:text-gray-400"
              >
                No scores recorded
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
);

interface ScoreListProps {
  scores: any[];
  onUpload: () => void;
  markPlayer?: any;
  markPlayer2?: any;
  selfPlayer?: any;
  currentPlayer?: any;
  currentPlayer2?: any;
}
const ScoreList: React.FC<ScoreListProps> = ({
  scores,
  onUpload,
  markPlayer,
  markPlayer2,
}) => {
  const selfScores = scores.filter((s) => s.scoreByType === "self");
  const partnerScores = scores.filter((s) => s.scoreByType === "mark");
  const pendingScores = scores.filter((s) => !s.isUploaded);

  return (
    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg shadow-md p-6">
      <h3 className="text-lg font-semibold mb-6 text-gray-900 dark:text-white">
        Score Summary
      </h3>

      <ScoreTable scores={selfScores} title="Your Scores" title2="" />
      {partnerScores.length > 0 && (
        <ScoreTable
          scores={partnerScores}
          title={`${
            markPlayer
              ? markPlayer.firstName + " " + markPlayer.lastName + " scores"
              : "Partner scores"
          }`}
          title2={
            markPlayer2
              ? ` and ${
                  markPlayer2.firstName + " " + markPlayer2.lastName
                } scores`
              : ""
          }
        />
      )}

      {pendingScores.length > 0 && (
        <button
          onClick={onUpload}
          className="mt-6 w-full bg-green-600 hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600 text-white py-2 px-4 rounded-lg font-medium flex items-center justify-center transition-colors duration-200"
        >
          <Upload className="h-5 w-5 mr-2" />
          Upload {pendingScores.length} Pending{" "}
          {pendingScores.length === 1 ? "Score" : "Scores"}
        </button>
      )}
    </div>
  );
};

export const ScoreEntryPlayer: React.FC<any> = () => {
  const { currentUser } = useAuthStore();

  const {
    allDetailsForScoreEntry,
    addScore,
    updateScore,
    uploadScoresByPlayer,
    getScoresToStartEntry,
  } = useScoresStore();

  const [currentHole, setCurrentHole] = useState(1);
  const [score, setScore] = useState<number | "">("");
  const [partnerScore, setPartnerScore] = useState<number | "">("");

  const { openDialog } = useConfirmationDialog();

  // Get the scores for the current player
  useEffect(() => {
    if (currentUser) {
      getScoresToStartEntry(currentUser.playerId ?? "");
    }
  }, [currentUser, getScoresToStartEntry]);

  useEffect(() => {
    if (allDetailsForScoreEntry && allDetailsForScoreEntry.flightDetails) {
      if (allDetailsForScoreEntry.scores.length === 0) {
        const startHole = (
          allDetailsForScoreEntry.flightDetails as FlightDetails
        )?.holeNumber;
        setCurrentHole(startHole);
      } else {
        let nextHole = (allDetailsForScoreEntry.flightDetails as FlightDetails)
          ?.holeNumber;
        let attempts = 0;
        while (
          allDetailsForScoreEntry.scores.some(
            (s: any) => s.holeNumber === nextHole
          )
        ) {
          nextHole = nextHole < 18 ? nextHole + 1 : 1;
          attempts++;
          if (
            nextHole ===
              (allDetailsForScoreEntry.flightDetails as FlightDetails)
                ?.holeNumber ||
            attempts >= 36
          )
            break;
        }
        setCurrentHole(nextHole);
      }
    }
  }, [allDetailsForScoreEntry]);

  // Yardage data for each hole
  const getCurrentHoleData = (holeNumber: number) => {
    if (!allDetailsForScoreEntry) return null;
    const hole = allDetailsForScoreEntry.holes.find(
      (h: any) => h.holeNumber === holeNumber
    );
    return {
      holeNumber: hole?.holeNumber,
      par: hole?.par || 10,
      men: hole?.yardages.men.yardage || 4000,
      senior: hole?.yardages.seniors.yardage || 3800,
      ladies: hole?.yardages.ladies.yardage || 3600,
    };
  };

  const handlePrevHole = () => {
    handleSaveScore(false);

    if (currentHole > 1) {
      setCurrentHole((prev) => prev - 1);
    } else if (currentHole === 1) {
      setCurrentHole(18);
    }
  };

  const handleNextHole = (shouldSave = false) => {
    if (shouldSave) {
      handleSaveScore(false);
    }

    if (currentHole < 18) {
      setCurrentHole((prev) => prev + 1);
    } else if (currentHole === 18) {
      setCurrentHole(1);
    }
  };

  useEffect(() => {
    if (allDetailsForScoreEntry && allDetailsForScoreEntry.scores.length > 0) {
      const scores = allDetailsForScoreEntry.scores as Score[];
      const currentScore = scores.find(
        (s: Score) =>
          s.holeNumber === currentHole &&
          (s.scoreByPlayerId === currentUser?.playerId ||
            s.scoreByPlayer2Id === currentUser?.playerId)
      );
      const currentPartnerScore = scores.find(
        (s) =>
          s.holeNumber === currentHole &&
          (s.scoreForPlayerId ===
            (allDetailsForScoreEntry.flightDetails as FlightDetails)
              ?.markPlayerId ||
            s.scoreForPlayer2Id ===
              (allDetailsForScoreEntry.flightDetails as FlightDetails)
                ?.markPlayerId)
      );

      if (currentScore) {
        setScore(currentScore.score);
      } else {
        setScore("");
      }

      if (currentPartnerScore) {
        setPartnerScore(currentPartnerScore.score);
      } else {
        setPartnerScore("");
      }
    }
  }, [allDetailsForScoreEntry, currentHole, currentUser]);

  const handleScoreChange = (
    increment: boolean,
    isPartner: boolean = false
  ) => {
    if (isPartner) {
      if (partnerScore === "") {
        setPartnerScore(increment ? 1 : 12);
      } else {
        const newScore = increment
          ? Math.min((partnerScore as number) + 1, 12)
          : Math.max((partnerScore as number) - 1, 1);
        setPartnerScore(newScore);
      }
    } else {
      if (score === "") {
        setScore(increment ? 1 : 12);
      } else {
        const newScore = increment
          ? Math.min((score as number) + 1, 12)
          : Math.max((score as number) - 1, 1);
        setScore(newScore);
      }
    }
  };

  const handleSaveScore = (setNext = true) => {
    // check if the score already exists in the collection scoresByPlayer. If it does, confirm a change of score and update
    if (!allDetailsForScoreEntry || !currentUser) return;
    const scores = (allDetailsForScoreEntry.scores ?? []) as Score[];
    // for player scores.
    let confirmAndUpdatePlayerScore = false;
    let confirmAndUpdatePartnerScore = false;
    let existingScore = null;
    let existingPartnerScore = null;

    if (score !== "") {
      existingScore = scores.find((s) => {
        return (
          s.holeNumber === currentHole &&
          s.scoreByType === "self" &&
          (allDetailsForScoreEntry.flightDetails as FlightDetails)?.playerId ===
            s.scoreByPlayerId &&
          (allDetailsForScoreEntry.flightDetails as FlightDetails)?.playerId ===
            s.scoreForPlayerId
        );
      });
      if (existingScore) {
        if (existingScore.score !== score) {
          confirmAndUpdatePlayerScore = true;
        }
      } else {
        // add new score for player.
        addScore({
          id: uuid(),
          matchId: allDetailsForScoreEntry?.matchSetupConfig?.matchId ?? "",
          seasonId: allDetailsForScoreEntry?.matchSetupConfig?.seasonId ?? "",
          holeNumber: currentHole ?? 0,
          holeDescription: currentHole?.toString() ?? "",
          scoreDateTime: formatToTimeStamp(new Date()),
          score: score as number,
          scoreType: "stroke",
          scoreStatus: "complete",
          scoreComment: "",
          scoreByType: "self",
          scoreByPlayerId:
            (allDetailsForScoreEntry.flightDetails as FlightDetails)
              ?.playerId ?? "",
          scoreByPlayer2Id:
            (allDetailsForScoreEntry.flightDetails as FlightDetails)
              ?.player2Id ?? "",
          scoreForPlayerId:
            (allDetailsForScoreEntry.flightDetails as FlightDetails)
              ?.playerId ?? "",
          scoreForPlayer2Id:
            (allDetailsForScoreEntry.flightDetails as FlightDetails)
              ?.player2Id ?? "",
          scoreByTeamId:
            (allDetailsForScoreEntry.flightDetails as FlightDetails)?.teamId ??
            "",
          scoreForTeamId:
            (allDetailsForScoreEntry.flightDetails as FlightDetails)?.teamId ??
            "",
          isUploaded: false,
          uploadDateTime: "",
          scoreEnteredByPlayerId: currentUser.playerId,
        });
      }
    }

    if (partnerScore !== "") {
      existingPartnerScore = scores.find((s) => {
        return (
          s.holeNumber === currentHole &&
          s.scoreByType === "mark" &&
          (allDetailsForScoreEntry.flightDetails as FlightDetails)?.playerId ===
            s.scoreByPlayerId &&
          (allDetailsForScoreEntry.flightDetails as FlightDetails)
            ?.markPlayerId === s.scoreForPlayerId
        );
      });
      if (existingPartnerScore) {
        if (existingPartnerScore.score !== partnerScore) {
          confirmAndUpdatePartnerScore = true;
        }
      } else {
        // add new score for partner.
        addScore({
          id: uuid(),
          matchId: allDetailsForScoreEntry.matchSetupConfig?.matchId ?? "",
          seasonId: allDetailsForScoreEntry.matchSetupConfig?.seasonId ?? "",
          holeNumber: currentHole,
          holeDescription: currentHole.toString(),
          scoreDateTime: formatToTimeStamp(new Date()),
          score: partnerScore as number,
          scoreType: "stroke",
          scoreStatus: "complete",
          scoreComment: "",
          scoreByType: "mark",
          scoreByPlayerId:
            (allDetailsForScoreEntry.flightDetails as FlightDetails)
              ?.playerId ?? "",
          scoreByPlayer2Id:
            (allDetailsForScoreEntry.flightDetails as FlightDetails)
              ?.player2Id ?? "",
          scoreForPlayerId:
            (allDetailsForScoreEntry.flightDetails as FlightDetails)
              ?.markPlayerId ?? "",
          scoreForPlayer2Id:
            (allDetailsForScoreEntry.flightDetails as FlightDetails)
              ?.markPlayer2Id ?? "",
          scoreByTeamId:
            (allDetailsForScoreEntry.flightDetails as FlightDetails)?.teamId ??
            "",
          scoreForTeamId:
            (allDetailsForScoreEntry.flightDetails as FlightDetails)
              ?.markTeamId ?? "",
          isUploaded: false,
          uploadDateTime: "",
          scoreEnteredByPlayerId: currentUser.playerId,
        });
      }
    }

    if (confirmAndUpdatePlayerScore || confirmAndUpdatePartnerScore) {
      openDialog({
        title: "Update Score",
        message: `You have already entered a score for this hole. Do you want to update the scores?`,
        onConfirm: () => {
          if (confirmAndUpdatePlayerScore && existingScore) {
            updateScore({
              ...existingScore,
              score: score as number,
              isUploaded: false,
            });
          }
          if (confirmAndUpdatePartnerScore && existingPartnerScore) {
            updateScore({
              ...existingPartnerScore,
              score: partnerScore as number,
              isUploaded: false,
            });
          }
        },
      });
    }

    if (setNext) handleNextHole(false);
  };

  const handleUploadScores = () => {
    uploadScoresByPlayer();
  };

  return (
    <div className="py-0 px-1 pb-6">
      {!allDetailsForScoreEntry && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 mb-8">
          <div className="text-center text-gray-900 dark:text-white">
            <Trophy className="h-12 w-12 text-blue-500" />
            <h2 className="text-xl font-semibold mb-2">No Match in Progress - score entry player.</h2>
            <p className="text-gray-500 dark:text-gray-400">
              You are not currently in a match. Please check back later.
            </p>
          </div>
        </div>
      )}
      {allDetailsForScoreEntry &&
        allDetailsForScoreEntry.matchSetupConfig &&
        allDetailsForScoreEntry.matchSetupConfig.gameFormat === "matchplay" && (
          <MatchPlayScoreEntry
            allDetailsForScoreEntry={allDetailsForScoreEntry}
          />
        )}

      {allDetailsForScoreEntry &&
        allDetailsForScoreEntry.matchSetupConfig &&
        allDetailsForScoreEntry.matchSetupConfig.gameFormat !== "matchplay" &&
        allDetailsForScoreEntry.flightDetails && (
          <>
            {/* Hole Navigation */}
            <div className="bg-white dark:bg-gray-800 rounded-sm shadow-md p-4 ">
              <div className="text-center">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  {allDetailsForScoreEntry.matchSetupConfig.courseName} -{" "}
                  {showDateTimeShort(
                    `${allDetailsForScoreEntry.matchSetupConfig.date}${allDetailsForScoreEntry.matchSetupConfig.time}`
                  )}
                </h2>
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 mb-8">
              <div className="flex flex-col items-center">
                <div className="flex items-center justify-between w-full mb-4">
                  <button
                    onClick={handlePrevHole}
                    className="p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </button>
                  <div className="text-2xl font-bold text-gray-900 dark:text-white flex justify-center items-center space-x-4">
                    <span>Hole {currentHole}</span>
                    <div className="text-lg text-gray-500 dark:text-gray-400">
                      Par {getCurrentHoleData(currentHole)?.par}
                    </div>
                  </div>
                  <button
                    onClick={() => handleNextHole(true)}
                    className="p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
                  >
                    <ChevronRight className="h-6 w-6" />
                  </button>
                </div>

                <div className="text-center mb-6">
                  <div className="mt-1 flex justify-center space-x-4 text-sm">
                    <span className="text-blue-600 dark:text-blue-400 font-medium">
                      Men: {getCurrentHoleData(currentHole)?.men}y
                    </span>
                    <span className="text-yellow-600 dark:text-yellow-400 font-medium">
                      Senior: {getCurrentHoleData(currentHole)?.senior}y
                    </span>
                    <span className="text-pink-600 dark:text-pink-400 font-medium">
                      Ladies: {getCurrentHoleData(currentHole)?.ladies}y
                    </span>
                  </div>
                </div>

                {/* Score Input */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6 w-full max-w-2xl">
                  <div>
                    <div className="flex flex-col items-center mb-2">
                      <PlayerRecord
                        member={
                          (
                            allDetailsForScoreEntry.flightDetails as FlightDetails
                          )?.player
                        }
                        showHandicapAndMsl={false}
                      />
                      {(allDetailsForScoreEntry.flightDetails as FlightDetails)
                        ?.player2 && (
                        <PlayerRecord
                          member={
                            (
                              allDetailsForScoreEntry.flightDetails as FlightDetails
                            )?.player2
                          }
                          showHandicapAndMsl={false}
                        />
                      )}
                    </div>
                    <div className="flex items-center justify-center space-x-4">
                      <button
                        onClick={() => handleScoreChange(false)}
                        className="w-12 h-12 flex items-center justify-center text-2xl font-bold bg-gray-100 dark:bg-gray-700 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"
                      >
                        -
                      </button>
                      <div className="w-20 text-center text-3xl font-bold text-gray-900 dark:text-white">
                        {score || "-"}
                      </div>
                      <button
                        onClick={() => handleScoreChange(true)}
                        className="w-12 h-12 flex items-center justify-center text-2xl font-bold bg-gray-100 dark:bg-gray-700 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"
                      >
                        +
                      </button>
                    </div>
                  </div>

                  {allDetailsForScoreEntry &&
                    (allDetailsForScoreEntry.flightDetails as FlightDetails)
                      ?.markPlayer && (
                      <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                        <div className="flex flex-col items-center mb-2">
                          <PlayerRecord
                            member={
                              (
                                allDetailsForScoreEntry.flightDetails as FlightDetails
                              )?.markPlayer
                            }
                            showHandicapAndMsl={false}
                          />
                          {(
                            allDetailsForScoreEntry.flightDetails as FlightDetails
                          )?.markPlayer2 && (
                            <PlayerRecord
                              member={
                                (
                                  allDetailsForScoreEntry.flightDetails as FlightDetails
                                )?.markPlayer2
                              }
                              showHandicapAndMsl={false}
                            />
                          )}
                        </div>
                        <div className="flex items-center justify-center space-x-6">
                          <button
                            onClick={() => handleScoreChange(false, true)}
                            className="w-12 h-12 flex items-center justify-center text-2xl font-bold bg-gray-100 dark:bg-gray-700 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"
                          >
                            -
                          </button>
                          <div className="w-20 text-center text-4xl font-bold text-gray-900 dark:text-white">
                            {partnerScore || "-"}
                          </div>
                          <button
                            onClick={() => handleScoreChange(true, true)}
                            className="w-12 h-12 flex items-center justify-center text-2xl font-bold bg-gray-100 dark:bg-gray-700 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"
                          >
                            +
                          </button>
                        </div>
                      </div>
                    )}
                </div>

                <button
                  onClick={() => handleSaveScore()}
                  disabled={
                    score === "" ||
                    ((allDetailsForScoreEntry.flightDetails as FlightDetails)
                      ?.markPlayer !== null &&
                      partnerScore === "")
                  }
                  className="w-full bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white py-3 rounded-lg font-medium flex items-center justify-center disabled:opacity-50 transition-colors"
                >
                  <Save className="h-5 w-5 mr-2" />
                  Save Score
                </button>
              </div>
            </div>

            <div className="mt-8">
              <ScoreList
                scores={allDetailsForScoreEntry.scores}
                onUpload={handleUploadScores}
                markPlayer={
                  (allDetailsForScoreEntry.flightDetails as FlightDetails)
                    ?.markPlayer
                }
                markPlayer2={
                  (allDetailsForScoreEntry.flightDetails as FlightDetails)
                    ?.markPlayer2
                }
              />
            </div>
          </>
        )}
    </div>
  );
};
